import React, { useState, useMemo } from 'react';
import type {
  Recommendation,
  ImplementationStep,
  GovernanceImplementationStep,
} from '../types/bridgeIntelligence';
import { CausalFlowDiagram } from './CausalFlowDiagram';
import { CitationList } from './literature/CitationList';
import {
  RoleAssignmentModal,
  RACIBuilderModal,
  PolicyEditorModal,
  useCompletedActions,
  type GovernanceAction,
  type RecommendationSummary,
  type AssignedRole,
} from './governance';
import '../styles/recommendation-card.css';

interface RecommendationCardProps {
  recommendation: Recommendation;
  journey_id: string;
  /** All recommendations from Bridge AI, passed to RACI modal for dynamic row population */
  allRecommendations?: Recommendation[];
}

// COMPASS_UI_STANDARD: WCAG AA compliant priority styling
const getPriorityConfig = (priority: string) => {
  switch (priority.toLowerCase()) {
    case 'critical':
      return { label: 'CRITICAL', chipClass: 'chip-critical' };
    case 'warning':
      return { label: 'WARNING', chipClass: 'chip-warning' };
    default:
      return { label: 'OPTIMIZATION', chipClass: 'chip-optimization' };
  }
};

// Sprint 9: Provenance badge configuration
const getProvenanceConfig = (provenance?: string) => {
  switch (provenance) {
    case 'journey':
      return {
        label: 'Journey',
        icon: '🚂',
        chipClass: 'provenance-journey',
        tooltip: 'Based on dataset analysis from this journey',
      };
    case 'organization':
      return {
        label: 'Organization',
        icon: '🏢',
        chipClass: 'provenance-organization',
        tooltip: 'Based on organization-level assessments (interviews, documents, standards)',
      };
    case 'merged':
      return {
        label: 'Merged',
        icon: '🔀',
        chipClass: 'provenance-merged',
        tooltip: 'Combines insights from both journey analysis and organization context',
      };
    default:
      return null;
  }
};

// DC-1093: Persona name to abbreviation mapping for governance buttons
function getPersonaAbbreviation(name: string): string {
  const map: Record<string, string> = {
    'Data Owner': 'DO',
    'Data Steward': 'DS',
    'Data Scientist': 'DAS',
    'Document Owner': 'DOO',
    'Legal Data Expert': 'LDE',
    'Data Governance Expert': 'DGE',
    'Subject Matter Expert': 'SME',
  };
  return map[name] || name.split(' ').map(w => w[0]).join('');
}

// DC-1093: Helper to check if a step has a governance action
const isGovernanceStep = (
  step: string | ImplementationStep | GovernanceImplementationStep
): step is GovernanceImplementationStep => {
  return typeof step === 'object' && 'governance_action' in step && !!step.governance_action;
};

// DC-1093: Get governance steps from recommendation
const getGovernanceSteps = (
  steps: (string | ImplementationStep | GovernanceImplementationStep)[] | undefined
): GovernanceImplementationStep[] => {
  if (!steps) return [];
  return steps.filter(isGovernanceStep);
};

export const RecommendationCard: React.FC<RecommendationCardProps> = ({
  recommendation,
  journey_id,
  allRecommendations,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

  // DC-1093: Completion state tracking for governance actions
  const { isStepCompleted, actions: completedActions, refetch: refetchActions } = useCompletedActions(journey_id);

  // Build recommendation summaries for RACI modal
  const recommendationSummaries: RecommendationSummary[] = useMemo(() => {
    if (!allRecommendations) return [];
    return allRecommendations.map(r => ({
      id: r.id,
      title: r.title,
      priority: r.priority,
      affected_stations: r.affected_stations,
    }));
  }, [allRecommendations]);

  // Build assigned roles from completed assign_role actions
  const assignedRoles: AssignedRole[] = useMemo(() => {
    return completedActions
      .filter(a => a.action_type === 'assign_role' && a.status === 'completed')
      .map(a => {
        const data = a.action_data as Record<string, unknown>;
        return {
          persona_id: (data.persona_id as string) || '',
          persona_name: (data.persona_name as string) || '',
          employee_id: (data.employee_id as string) || undefined,
          employee_name: (data.employee_name as string) || undefined,
        };
      });
  }, [completedActions]);

  // DC-1093: Governance modal state
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [raciModalOpen, setRaciModalOpen] = useState(false);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [activeGovernanceStep, setActiveGovernanceStep] = useState<GovernanceImplementationStep | null>(null);

  const priorityConfig = getPriorityConfig(recommendation.priority);
  const provenanceConfig = getProvenanceConfig(recommendation.provenance);

  // DC-1093: Get governance steps for this recommendation
  const governanceSteps = getGovernanceSteps(recommendation.implementation_steps);
  const hasGovernanceActions = governanceSteps.length > 0;

  // DC-1093: Handle governance action button clicks
  const handleGovernanceAction = (step: GovernanceImplementationStep, e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveGovernanceStep(step);

    switch (step.governance_action?.type) {
      case 'assign_role':
        setRoleModalOpen(true);
        break;
      case 'create_raci':
        setRaciModalOpen(true);
        break;
      case 'use_template':
        setPolicyModalOpen(true);
        break;
      case 'review_policy':
        // Navigate to policy document
        if (step.governance_action?.policy_document_id) {
          window.location.href = `/settings/governance/documents/${step.governance_action.policy_document_id}`;
        }
        break;
    }
  };

  // DC-1093: Handle governance action success
  const handleGovernanceSuccess = (action: GovernanceAction) => {
    console.log('[RecommendationCard] Governance action completed:', action);
    refetchActions();
  };

  const handleFeedback = async (type: 'positive' | 'negative') => {
    if (feedbackSubmitting) return;
    setFeedbackSubmitting(true);
    try {
      await fetch('/api/bridge/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          journey_id,
          recommendation_id: recommendation.id,
          feedback_type: type,
        }),
      });

      setFeedbackGiven(true);
      setTimeout(() => setFeedbackGiven(false), 2000);
    } catch (error) {
      console.error('[BridgeAI] Feedback submission error:', error);
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  return (
    <article
      className={`recommendation-card priority-${recommendation.priority}`}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="card-header">
        <div className="card-title">
          {/* Status Chip - Replaces emoji for WCAG AA compliance */}
          <span className={`status-chip ${priorityConfig.chipClass}`}>
            {priorityConfig.label}
          </span>
          {/* Sprint 9: Provenance Badge */}
          {provenanceConfig && (
            <span
              className={`provenance-badge ${provenanceConfig.chipClass}`}
              title={provenanceConfig.tooltip}
            >
              <span className="provenance-icon">{provenanceConfig.icon}</span>
              {provenanceConfig.label}
            </span>
          )}
          <h4>{recommendation.title}</h4>
        </div>
        <button
          className="expand-btn"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? '▲' : '▼'}
        </button>
      </div>

      <div className="card-metadata">
        <span className="metadata-item">
          Impact: <strong>{recommendation.impact}</strong>
        </span>
        <span className="metadata-item">
          Effort: <strong>{recommendation.effort}</strong>
        </span>
        <span className="metadata-item">
          Timeline: <strong>{recommendation.timeline}</strong>
        </span>
      </div>

      {/* Financial ROI Summary - calculated from Pistoia driver + effort/timeline */}
      {recommendation.financial_summary && (
        <div className="card-financial-summary">
          <span className="financial-item return">
            Return: <strong>${(recommendation.financial_summary.projected_return_usd / 1000).toFixed(0)}K</strong>
          </span>
          <span className="financial-item cost">
            Cost: <strong>${(recommendation.financial_summary.implementation_cost_usd / 1000).toFixed(0)}K</strong>
          </span>
          <span className="financial-item roi">
            ROI: <strong>${(recommendation.financial_summary.net_roi_usd / 1000).toFixed(0)}K</strong>
          </span>
          <span className="financial-item payback">
            Payback: <strong>{recommendation.financial_summary.payback_weeks} wks</strong>
          </span>
        </div>
      )}

      {/* User Feedback (v1.1) */}
      <div className="recommendation-feedback" onClick={(e) => e.stopPropagation()}>
        {feedbackGiven ? (
          <span className="feedback-thanks">Thanks for your feedback!</span>
        ) : (
          <>
            <span className="feedback-label">Was this helpful?</span>
            <button
              className="feedback-btn positive"
              onClick={() => handleFeedback('positive')}
              disabled={feedbackSubmitting}
              aria-label="Mark as helpful"
              title="Helpful"
            >
              👍
            </button>
            <button
              className="feedback-btn negative"
              onClick={() => handleFeedback('negative')}
              disabled={feedbackSubmitting}
              aria-label="Mark as not helpful"
              title="Not helpful"
            >
              👎
            </button>
          </>
        )}
      </div>

      {expanded && (
        <div className="card-details">
          <p className="description">{recommendation.description}</p>

          {/* Pistoia Business Value Driver (Sprint 8) */}
          {recommendation.pistoia_driver && (
            <div className="pistoia-driver-section">
              <h5>Business Value Driver</h5>
              <div className="driver-info">
                <span className="driver-name">{recommendation.pistoia_driver.driver_name}</span>
                <span className="driver-id">({recommendation.pistoia_driver.driver_id})</span>
              </div>
              {recommendation.pistoia_driver.calculation_parameters && (
                <div className="calculation-params">
                  <table className="params-table">
                    <tbody>
                      {recommendation.pistoia_driver.calculation_parameters.time_baseline !== undefined && (
                        <tr>
                          <td className="param-label">Current State:</td>
                          <td className="param-value">{recommendation.pistoia_driver.calculation_parameters.time_baseline} hrs/search</td>
                        </tr>
                      )}
                      {recommendation.pistoia_driver.calculation_parameters.time_FAIR !== undefined && (
                        <tr>
                          <td className="param-label">Target State:</td>
                          <td className="param-value">{recommendation.pistoia_driver.calculation_parameters.time_FAIR} hrs/search</td>
                        </tr>
                      )}
                      {recommendation.pistoia_driver.calculation_parameters.requests_per_year !== undefined && (
                        <tr>
                          <td className="param-label">Annual Requests:</td>
                          <td className="param-value">{recommendation.pistoia_driver.calculation_parameters.requests_per_year.toLocaleString()}</td>
                        </tr>
                      )}
                      {recommendation.pistoia_driver.calculation_parameters.dataset_count !== undefined && (
                        <tr>
                          <td className="param-label">Datasets Affected:</td>
                          <td className="param-value">{recommendation.pistoia_driver.calculation_parameters.dataset_count}</td>
                        </tr>
                      )}
                      {recommendation.pistoia_driver.calculation_parameters.percentage_baseline !== undefined && (
                        <tr>
                          <td className="param-label">Current Score:</td>
                          <td className="param-value">{recommendation.pistoia_driver.calculation_parameters.percentage_baseline}%</td>
                        </tr>
                      )}
                      {recommendation.pistoia_driver.calculation_parameters.percentage_target !== undefined && (
                        <tr>
                          <td className="param-label">Target Score:</td>
                          <td className="param-value">{recommendation.pistoia_driver.calculation_parameters.percentage_target}%</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Impact Assessment (Sprint 8) */}
          {recommendation.impact_assessment && (
            <div className="impact-assessment-section">
              <h5>Impact Assessment</h5>
              {recommendation.impact_assessment.current_state_description && (
                <p className="state-description">
                  <strong>Current:</strong> {recommendation.impact_assessment.current_state_description}
                </p>
              )}
              {recommendation.impact_assessment.target_state_description && (
                <p className="state-description">
                  <strong>Target:</strong> {recommendation.impact_assessment.target_state_description}
                </p>
              )}
              {recommendation.impact_assessment.datasets_affected && (
                <p className="datasets-affected">
                  <strong>Datasets Affected:</strong> {recommendation.impact_assessment.datasets_affected}
                </p>
              )}
            </div>
          )}

          {/* Stakeholder Context (Sprint 8) */}
          {recommendation.stakeholder_context && (
            <div className="stakeholder-section">
              <h5>Stakeholder Readiness</h5>
              <div className="stakeholder-info">
                {recommendation.stakeholder_context.readiness && (
                  <span className={`readiness-badge readiness-${recommendation.stakeholder_context.readiness}`}>
                    Readiness: {recommendation.stakeholder_context.readiness}
                  </span>
                )}
                {(recommendation.stakeholder_context.champions?.length ?? 0) > 0 && (
                  <p className="champions">
                    <strong>Champions:</strong> {recommendation.stakeholder_context.champions!.join(', ')}
                  </p>
                )}
                {(recommendation.stakeholder_context.resistance?.length ?? 0) > 0 && (
                  <p className="resistance">
                    <strong>Potential Resistance:</strong> {recommendation.stakeholder_context.resistance!.join(', ')}
                  </p>
                )}
                {(recommendation.stakeholder_context.key_concerns?.length ?? 0) > 0 && (
                  <p className="concerns">
                    <strong>Key Concerns:</strong> {recommendation.stakeholder_context.key_concerns!.join(', ')}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Rationale (v1.1) */}
          {recommendation.rationale && (
            <div className="rationale-section">
              <h5>Why this recommendation?</h5>
              <p className="rationale-text">{recommendation.rationale}</p>
            </div>
          )}

          {/* DC-913: Causal Analysis Flow Diagram */}
          {recommendation.causal_analysis && (
            <CausalFlowDiagram
              causalAnalysis={recommendation.causal_analysis}
              compact={false}
            />
          )}

          {/* DC-1007: Supporting Literature Section */}
          {recommendation.literature_support &&
           recommendation.literature_support.citations.length > 0 && (
            <div className="literature-support-section">
              <CitationList
                literatureSupport={recommendation.literature_support}
                maxVisible={3}
                defaultCollapsed={true}
              />
            </div>
          )}

          {/* Sprint 9: Provenance Details Panel */}
          {provenanceConfig && recommendation.provenance_details && (
            <div className="provenance-details-section">
              <h5>
                <span className="provenance-icon-lg">{provenanceConfig.icon}</span>
                Data Sources ({provenanceConfig.label})
              </h5>
              <div className="provenance-sources">
                {recommendation.provenance_details.journey_contribution &&
                 recommendation.provenance_details.journey_contribution.length > 0 && (
                  <div className="provenance-source-group">
                    <span className="source-group-label">Journey Stations:</span>
                    <div className="source-tags">
                      {recommendation.provenance_details.journey_contribution.map((station, idx) => (
                        <span key={idx} className="source-tag source-tag-journey">
                          {station}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {recommendation.provenance_details.org_contribution &&
                 recommendation.provenance_details.org_contribution.length > 0 && (
                  <div className="provenance-source-group">
                    <span className="source-group-label">Organization Context:</span>
                    <div className="source-tags">
                      {recommendation.provenance_details.org_contribution.map((source, idx) => (
                        <span key={idx} className="source-tag source-tag-org">
                          {source}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Data Lineage / Provenance (Sprint 8 - Consolidation Tracking) */}
          {(recommendation.source_recommendations?.length ?? 0) > 0 && (
            <div className="lineage-section">
              <h5>Data Lineage ({recommendation.source_recommendations!.length} source recommendations)</h5>
              {recommendation.synthesis_explanation && (
                <p className="synthesis-explanation">
                  <em>{recommendation.synthesis_explanation}</em>
                </p>
              )}
              <div className="source-recommendations">
                {recommendation.source_recommendations!.map((src, idx) => (
                  <div
                    key={idx}
                    className={`source-rec source-contribution-${src.contribution || 'supporting'}`}
                  >
                    <span className="source-station">{src.station_id}</span>
                    <span className="source-title">{src.title}</span>
                    <span className={`source-priority priority-${src.priority}`}>
                      {src.priority}
                    </span>
                    <span className={`contribution-badge contribution-${src.contribution || 'supporting'}`}>
                      {src.contribution || 'supporting'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Implementation Steps */}
          {(recommendation.implementation_steps?.length ?? 0) > 0 && (
            <div className="implementation-steps">
              <h5>Implementation Steps:</h5>
              <ol>
                {recommendation.implementation_steps!.map((step, idx) => {
                  const stepObj = typeof step === 'string' ? { step, complexity: undefined } : step;
                  return (
                    <li key={idx} className="step-item">
                      <span className="step-text">{stepObj.step}</span>
                      {stepObj.complexity && (
                        <span className={`complexity-badge complexity-${stepObj.complexity}`}>
                          {stepObj.complexity}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {/* Related FAIR Rules */}
          {(recommendation.related_fair_rules?.length ?? 0) > 0 && (
            <div className="related-rules">
              <strong>Related FAIR Rules:</strong>{' '}
              {recommendation.related_fair_rules!.join(', ')}
            </div>
          )}

          {/* Affected Stations */}
          {(recommendation.affected_stations?.length ?? 0) > 0 && (
            <div className="affected-stations">
              <strong>Affects:</strong> {recommendation.affected_stations!.length}{' '}
              station(s)
            </div>
          )}

          <div className="card-actions">
            {/* DC-1093: Governance action buttons */}
            {hasGovernanceActions && (
              <div className="governance-actions" onClick={(e) => e.stopPropagation()}>
                {governanceSteps.map((step, idx) => {
                  const actionType = step.governance_action?.type;
                  if (!actionType) return null;

                  let buttonLabel = '';
                  let buttonClass = '';
                  const personaId = step.governance_action?.persona_id;

                  // Check completion state
                  const completedAction = isStepCompleted(
                    actionType,
                    recommendation.id,
                    personaId
                  );
                  const isCompleted = !!completedAction;

                  switch (actionType) {
                    case 'assign_role': {
                      const personaName = step.governance_action?.persona_name || '';
                      const abbrev = getPersonaAbbreviation(personaName);
                      if (isCompleted) {
                        buttonLabel = `${abbrev} Assigned`;
                      } else {
                        buttonLabel = abbrev ? `Assign ${abbrev}` : 'Assign Role';
                      }
                      buttonClass = 'assign-role';
                      break;
                    }
                    case 'create_raci':
                      buttonLabel = isCompleted ? 'RACI Created' : 'Create RACI';
                      buttonClass = 'create-raci';
                      break;
                    case 'use_template':
                      buttonLabel = isCompleted ? 'Policy Adopted' : 'Use Template';
                      buttonClass = 'use-template';
                      break;
                    case 'review_policy':
                      buttonLabel = isCompleted ? 'Policy Reviewed' : 'Review Policy';
                      buttonClass = 'review-policy';
                      break;
                  }

                  return (
                    <button
                      key={`gov-${idx}`}
                      className={`action-btn governance ${buttonClass}${isCompleted ? ' completed' : ''}`}
                      onClick={(e) => handleGovernanceAction(step, e)}
                      disabled={isCompleted}
                      title={isCompleted ? `Completed ${completedAction?.completed_at ? new Date(completedAction.completed_at).toLocaleDateString() : ''}` : ''}
                    >
                      {isCompleted && <span className="completed-check" aria-hidden="true">&#10003; </span>}
                      {buttonLabel}
                      {!isCompleted && <span> &rarr;</span>}
                    </button>
                  );
                })}
              </div>
            )}
            <button className="action-btn primary">View Station</button>
          </div>
        </div>
      )}

      {/* DC-1093: Governance Modals */}
      {roleModalOpen && activeGovernanceStep && (
        <RoleAssignmentModal
          isOpen={roleModalOpen}
          onClose={() => {
            setRoleModalOpen(false);
            setActiveGovernanceStep(null);
          }}
          onSuccess={handleGovernanceSuccess}
          journeyId={journey_id}
          recommendationId={recommendation.id}
          recommendationTitle={recommendation.title}
          personaId={activeGovernanceStep.governance_action?.persona_id}
          personaName={activeGovernanceStep.governance_action?.persona_name}
          suggestedEmployeeIds={activeGovernanceStep.governance_action?.suggested_employee_ids}
        />
      )}

      {raciModalOpen && activeGovernanceStep && (
        <RACIBuilderModal
          isOpen={raciModalOpen}
          onClose={() => {
            setRaciModalOpen(false);
            setActiveGovernanceStep(null);
          }}
          onSuccess={handleGovernanceSuccess}
          journeyId={journey_id}
          recommendationId={recommendation.id}
          recommendationTitle={recommendation.title}
          raciTemplateId={activeGovernanceStep.governance_action?.raci_template_id}
          allRecommendations={recommendationSummaries}
          assignedRoles={assignedRoles}
        />
      )}

      {policyModalOpen && activeGovernanceStep && (
        <PolicyEditorModal
          isOpen={policyModalOpen}
          onClose={() => {
            setPolicyModalOpen(false);
            setActiveGovernanceStep(null);
          }}
          onSuccess={handleGovernanceSuccess}
          journeyId={journey_id}
          recommendationId={recommendation.id}
          recommendationTitle={recommendation.title}
          policyTemplateId={activeGovernanceStep.governance_action?.policy_template_id}
          allRecommendations={recommendationSummaries}
        />
      )}
    </article>
  );
};
